import { useState, useEffect, useRef } from "react";
import {
  GraduationCap, BookOpen, Target, Award, Play, CheckCircle,
  Clock, User, Users, MessageSquare, Send, ArrowLeft, ChevronRight,
  Star, TrendingUp, Phone, FileText, Sparkles, RefreshCw, X,
  AlertTriangle, BarChart3, Brain, Mic, Volume2, Plus, Calendar,
  HelpCircle, ThumbsUp, ThumbsDown, Timer, Trophy, Zap, Download,
  ExternalLink, FolderOpen, FileCheck, Calculator, MapPin, Euro, Square
} from "lucide-react";
import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Plafonds de revenus 2026 pour le calculateur
const PLAFONDS_2026 = {
  idf: {
    1: { tres_modeste: 24031, modeste: 29253 },
    2: { tres_modeste: 35270, modeste: 42933 },
    3: { tres_modeste: 42357, modeste: 51564 },
    4: { tres_modeste: 49455, modeste: 60208 },
    5: { tres_modeste: 56580, modeste: 68877 }
  },
  province: {
    1: { tres_modeste: 17363, modeste: 22259 },
    2: { tres_modeste: 25393, modeste: 32553 },
    3: { tres_modeste: 30540, modeste: 39148 },
    4: { tres_modeste: 35676, modeste: 45735 },
    5: { tres_modeste: 40835, modeste: 52348 }
  }
};

// Participation client moyenne (données réelles)
const PARTICIPATION_MOYENNE = {
  tres_modeste: 2276,
  modeste: 3933
};

export default function FormationsDashboard({ onBack, authUser }) {
  const [activeTab, setActiveTab] = useState("overview"); // overview, modules, training, quiz, simulation, documents, calculateur, analytics
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pour les documents
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [selectedDocCategory, setSelectedDocCategory] = useState(null);
  
  // Pour le calculateur de reste à charge
  const [calcDevisHT, setCalcDevisHT] = useState(10000);
  const [calcZone, setCalcZone] = useState("province");
  const [calcPersonnes, setCalcPersonnes] = useState(1);
  const [calcRevenus, setCalcRevenus] = useState(18000);
  const [calcResult, setCalcResult] = useState(null);
  
  // Pour la session de formation
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const chatEndRef = useRef(null);
  
  // Pour l'analyse des appels
  const [callAnalysis, setCallAnalysis] = useState(null);
  const [analyzingCalls, setAnalyzingCalls] = useState(false);
  
  // Pour les exercices pratiques
  const [exerciseTab, setExerciseTab] = useState("pitch"); // pitch, objections
  const [pitchText, setPitchText] = useState("");
  const [pitchDuration, setPitchDuration] = useState(0);
  const [pitchTimer, setPitchTimer] = useState(null);
  const [pitchResult, setPitchResult] = useState(null);
  const [evaluatingPitch, setEvaluatingPitch] = useState(false);
  const [pitchListening, setPitchListening] = useState(false);
  const [pitchInterimText, setPitchInterimText] = useState("");
  const pitchRecognitionRef = useRef(null);
  const [objectionScenarios, setObjectionScenarios] = useState([]);
  const [selectedObjection, setSelectedObjection] = useState(null);
  const [objectionResponse, setObjectionResponse] = useState("");
  const [objectionResult, setObjectionResult] = useState(null);
  const [evaluatingObjection, setEvaluatingObjection] = useState(false);
  
  // Pour le Quiz interactif
  const [quizData, setQuizData] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizStartTime, setQuizStartTime] = useState(null);
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  
  // Pour la Simulation avec Fatima
  const [simulationScenarios, setSimulationScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [simulationMessages, setSimulationMessages] = useState([]);
  const [simulationInput, setSimulationInput] = useState("");
  const [simulationActive, setSimulationActive] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [sendingSimMessage, setSendingSimMessage] = useState(false);
  const simEndRef = useRef(null);
  
  // Mode Audio pour simulation
  const [simulationMode, setSimulationMode] = useState("text"); // "text", "audio_beginner", "audio_expert"
  const [isRecording, setIsRecording] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const audioRef = useRef(null);
  
  // Mode Expert - Conversation fluide
  const [expertModeActive, setExpertModeActive] = useState(false);
  const [silenceTimer, setSilenceTimer] = useState(null);
  const [audioContext, setAudioContext] = useState(null);
  const [analyser, setAnalyser] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const silenceTimeoutRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mediaRecorderRef = useRef(null); // Ref pour accéder au recorder dans VAD
  const audioStreamRef = useRef(null); // Ref pour accéder au stream dans les callbacks
  const vadResetRef = useRef(false); // Flag pour reset les variables VAD
  const recorderStartTimeRef = useRef(null); // Timestamp du démarrage du recorder
  
  // Determiner si l'utilisateur est manager (tous les utilisateurs authentifies voient toutes les formations)
  const isManager = !!authUser;
  const collaboratorEmail = authUser?.email || '';
  const collaboratorName = authUser?.name || authUser?.email?.split('@')[0] || 'Collaborateur';

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [modulesResp, progressResp, requestsResp] = await Promise.all([
        authFetch(`${API}/formations/modules?pole=telepro`),
        authFetch(`${API}/formations/collaborator/${encodeURIComponent(collaboratorEmail)}/progress`),
        authFetch(`${API}/formations/requests${isManager ? '' : `?collaborator_email=${encodeURIComponent(collaboratorEmail)}`}`)
      ]);

      if (modulesResp.ok) {
        const data = await modulesResp.json();
        setModules(data.modules || []);
      }

      if (progressResp.ok) {
        const data = await progressResp.json();
        setProgress(data);
      }

      if (requestsResp.ok) {
        const data = await requestsResp.json();
        setRequests(data.requests || []);
      }
    } catch (e) {
      console.error("Erreur chargement:", e);
    }
    setLoading(false);
  };

  const startModule = (module) => {
    setSelectedModule(module);
    setSelectedStep(module.steps?.[0] || null);
    setChatMessages([{
      role: "assistant",
      content: `Bonjour ${collaboratorName} ! 👋\n\nJe suis Fatima, ta formatrice pour le module "${module.title}".\n\nNous allons commencer par : **${module.steps?.[0]?.title || 'la première étape'}**\n\n${module.steps?.[0]?.content || 'Commençons !'}\n\nTu as des questions avant de commencer ?`
    }]);
    setActiveTab("training");
  };

  const sendTrainingMessage = async () => {
    if (!chatInput.trim() || !selectedModule || !selectedStep) return;

    const userMessage = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setSendingMessage(true);

    try {
      const resp = await authFetch(`${API}/formations/training-session?collaborator_email=${encodeURIComponent(collaboratorEmail)}&collaborator_name=${encodeURIComponent(collaboratorName)}&module_id=${selectedModule.module_id}&step_id=${selectedStep.step_id}&message=${encodeURIComponent(userMessage)}`, {
        method: "POST"
      });

      if (resp.ok) {
        const data = await resp.json();
        setChatMessages(prev => [...prev, { role: "assistant", content: data.response }]);
      }
    } catch (e) {
      console.error("Erreur envoi message:", e);
      setChatMessages(prev => [...prev, { role: "assistant", content: "Désolée, une erreur s'est produite. Réessaie !" }]);
    }
    setSendingMessage(false);
  };

  const completeStep = async () => {
    if (!selectedModule || !selectedStep) return;

    try {
      const resp = await authFetch(`${API}/formations/progress`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          collaborator_email: collaboratorEmail,
          module_id: selectedModule.module_id,
          step_id: selectedStep.step_id,
          status: "completed"
        })
      });

      if (resp.ok) {
        const data = await resp.json();
        
        // Passer à l'étape suivante
        const currentIndex = selectedModule.steps.findIndex(s => s.step_id === selectedStep.step_id);
        const nextStep = selectedModule.steps[currentIndex + 1];

        if (data.module_completed) {
          setChatMessages(prev => [...prev, {
            role: "assistant",
            content: `🎉 **Félicitations ${collaboratorName} !**\n\nTu as terminé le module "${selectedModule.title}" !\n\n🏆 Tu as obtenu le badge : **${data.badge_earned?.title}**\n\nContinue comme ça ! 💪`
          }]);
        } else if (nextStep) {
          setSelectedStep(nextStep);
          setChatMessages(prev => [...prev, {
            role: "assistant",
            content: `✅ Excellent ! Étape validée !\n\nPassons à la suite : **${nextStep.title}**\n\n${nextStep.content || 'Continuons !'}`
          }]);
        }

        await loadData();
      }
    } catch (e) {
      console.error("Erreur validation étape:", e);
    }
  };

  const analyzeMyalls = async () => {
    setAnalyzingCalls(true);
    setCallAnalysis(null);

    try {
      const resp = await authFetch(`${API}/formations/analyze-calls?collaborator_email=${encodeURIComponent(collaboratorEmail)}&collaborator_name=${encodeURIComponent(collaboratorName)}&limit=5`, {
        method: "POST"
      });

      if (resp.ok) {
        const data = await resp.json();
        setCallAnalysis(data);
      }
    } catch (e) {
      console.error("Erreur analyse appels:", e);
    }
    setAnalyzingCalls(false);
  };

  const requestFormation = async (moduleId = null) => {
    try {
      const resp = await authFetch(`${API}/formations/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          collaborator_email: collaboratorEmail,
          collaborator_name: collaboratorName,
          module_id: moduleId,
          request_type: "self",
          reason: "Demande de montée en compétences"
        })
      });

      if (resp.ok) {
        alert("Demande de formation envoyée !");
        await loadData();
      }
    } catch (e) {
      console.error("Erreur demande:", e);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // QUIZ INTERACTIF
  // ═══════════════════════════════════════════════════════════════

  const loadQuiz = async (moduleId) => {
    setActiveTab("quiz");
    try {
      const resp = await authFetch(`${API}/formations/quiz/${moduleId}`);
      if (resp.ok) {
        const data = await resp.json();
        setQuizData(data);
        setQuizAnswers({});
        setQuizResult(null);
        setCurrentQuizIndex(0);
        setQuizStartTime(Date.now());
      }
    } catch (e) {
      console.error("Erreur chargement quiz:", e);
    }
  };

  const submitQuizAnswer = (questionId, answer) => {
    setQuizAnswers(prev => ({ ...prev, [questionId]: answer }));
    
    // Passer au profil suivant après un délai
    if (quizData?.profiles && currentQuizIndex < quizData.profiles.length - 1) {
      setTimeout(() => setCurrentQuizIndex(prev => prev + 1), 300);
    }
  };

  const submitQuiz = async () => {
    if (!quizData) return;
    
    setSubmittingQuiz(true);
    const timeSpent = Math.round((Date.now() - quizStartTime) / 1000);
    
    // Préparer les réponses
    const answers = Object.entries(quizAnswers).map(([id, answer]) => ({
      question_id: parseInt(id),
      answer: answer
    }));
    
    try {
      const resp = await authFetch(`${API}/formations/quiz/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          collaborator_email: collaboratorEmail,
          module_id: quizData.module_id,
          answers: answers,
          time_spent_seconds: timeSpent
        })
      });
      
      if (resp.ok) {
        const result = await resp.json();
        setQuizResult(result);
      }
    } catch (e) {
      console.error("Erreur soumission quiz:", e);
    }
    setSubmittingQuiz(false);
  };

  const resetQuiz = () => {
    setQuizData(null);
    setQuizAnswers({});
    setQuizResult(null);
    setCurrentQuizIndex(0);
    setActiveTab("modules");
  };

  // ═══════════════════════════════════════════════════════════════
  // SIMULATION AVEC FATIMA
  // ═══════════════════════════════════════════════════════════════

  const loadSimulations = async () => {
    setActiveTab("simulation");
    try {
      const resp = await authFetch(`${API}/formations/simulations`);
      if (resp.ok) {
        const data = await resp.json();
        setSimulationScenarios(data.scenarios || []);
      }
    } catch (e) {
      console.error("Erreur chargement simulations:", e);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // DOCUMENTS DE FORMATION
  // ═══════════════════════════════════════════════════════════════

  const loadDocuments = async () => {
    setActiveTab("documents");
    setDocumentsLoading(true);
    try {
      const resp = await authFetch(`${API}/formations/documents`);
      if (resp.ok) {
        const data = await resp.json();
        setDocuments(data.documents || []);
      }
    } catch (e) {
      console.error("Erreur chargement documents:", e);
    } finally {
      setDocumentsLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // CALCULATEUR DE RESTE À CHARGE
  // ═══════════════════════════════════════════════════════════════

  const calculateResteACharge = () => {
    // Déterminer le profil selon les revenus
    const plafonds = PLAFONDS_2026[calcZone][Math.min(calcPersonnes, 5)];
    let profil = null;
    let taux = 0;
    let couleur = "";
    let emoji = "";

    if (calcRevenus <= plafonds.tres_modeste) {
      profil = "tres_modeste";
      taux = 70;
      couleur = "blue";
      emoji = "🔵";
    } else if (calcRevenus <= plafonds.modeste) {
      profil = "modeste";
      taux = 50;
      couleur = "yellow";
      emoji = "🟡";
    } else {
      profil = "non_eligible";
      taux = 0;
      couleur = "red";
      emoji = "❌";
    }

    // Calculs financiers
    const devisHT = parseFloat(calcDevisHT) || 0;
    const tva = devisHT * 0.10;
    const totalTTC = devisHT + tva;
    const subventionANAH = devisHT * (taux / 100);
    const resteAChargeTotal = totalTTC - subventionANAH;
    const participationMoyenne = profil !== "non_eligible" ? PARTICIPATION_MOYENNE[profil] : 0;
    const subventionsComplementaires = Math.max(0, resteAChargeTotal - participationMoyenne);

    setCalcResult({
      profil,
      taux,
      couleur,
      emoji,
      eligible: profil !== "non_eligible",
      devisHT,
      tva,
      totalTTC,
      subventionANAH,
      resteAChargeTotal,
      participationMoyenne,
      subventionsComplementaires,
      plafondUtilise: profil === "tres_modeste" ? plafonds.tres_modeste : plafonds.modeste,
      zone: calcZone === "idf" ? "Île-de-France" : "Province",
      personnes: calcPersonnes
    });
  };

  // Recalculer automatiquement quand les paramètres changent
  useEffect(() => {
    calculateResteACharge();
  }, [calcDevisHT, calcZone, calcPersonnes, calcRevenus]);

  // ═══════════════════════════════════════════════════════════════
  // EXERCICES PRATIQUES
  // ═══════════════════════════════════════════════════════════════

  const loadObjectionScenarios = async () => {
    setActiveTab("exercises");
    try {
      const resp = await authFetch(`${API}/formations/exercises/objections`);
      if (resp.ok) {
        const data = await resp.json();
        setObjectionScenarios(data.scenarios || []);
      }
    } catch (e) {
      console.error("Erreur chargement objections:", e);
    }
  };

  // Timer pour le pitch
  const startPitchTimer = () => {
    setPitchDuration(0);
    setPitchResult(null);
    const timer = setInterval(() => {
      setPitchDuration(prev => prev + 1);
    }, 1000);
    setPitchTimer(timer);
  };

  const stopPitchTimer = () => {
    if (pitchTimer) {
      clearInterval(pitchTimer);
      setPitchTimer(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PITCH - Reconnaissance vocale en temps réel
  // ═══════════════════════════════════════════════════════════════
  
  const startPitchSpeech = () => {
    // Vérifier si le navigateur supporte la reconnaissance vocale
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Votre navigateur ne supporte pas la reconnaissance vocale. Utilisez Chrome ou Edge.");
      return;
    }
    
    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.continuous = true;
    recognition.interimResults = true;
    
    let finalTranscript = '';
    
    recognition.onstart = () => {
      console.log("🎤 Reconnaissance vocale démarrée");
      setPitchListening(true);
      setPitchText("");
      setPitchInterimText("");
      setPitchResult(null);
      
      // Démarrer le timer
      setPitchDuration(0);
      const timer = setInterval(() => {
        setPitchDuration(prev => prev + 1);
      }, 1000);
      setPitchTimer(timer);
    };
    
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
          setPitchText(finalTranscript.trim());
        } else {
          interim += transcript;
        }
      }
      setPitchInterimText(interim);
    };
    
    recognition.onerror = (event) => {
      console.error("Erreur reconnaissance vocale:", event.error);
      if (event.error === 'no-speech') {
        // Continuer à écouter
        return;
      }
      setPitchListening(false);
      stopPitchTimer();
    };
    
    recognition.onend = () => {
      console.log("🎤 Reconnaissance vocale terminée");
      // Si on était encore en écoute (pas arrêt manuel), redémarrer
      if (pitchListening && pitchRecognitionRef.current) {
        try {
          recognition.start();
        } catch (e) {
          console.log("Fin de reconnaissance");
        }
      }
    };
    
    pitchRecognitionRef.current = recognition;
    recognition.start();
  };
  
  const stopPitchSpeech = async () => {
    console.log("🛑 Arrêt reconnaissance vocale");
    setPitchListening(false);
    
    if (pitchRecognitionRef.current) {
      pitchRecognitionRef.current.stop();
      pitchRecognitionRef.current = null;
    }
    
    stopPitchTimer();
    
    // Si on a du texte, soumettre automatiquement
    if (pitchText.trim()) {
      await submitPitch();
    }
  };

  const submitPitch = async () => {
    if (!pitchText.trim()) return;
    stopPitchTimer();
    setEvaluatingPitch(true);
    
    try {
      const resp = await authFetch(`${API}/formations/exercises/pitch/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          collaborator_email: collaboratorEmail,
          pitch_text: pitchText,
          duration_seconds: pitchDuration || 90
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setPitchResult(data.evaluation);
      }
    } catch (e) {
      console.error("Erreur soumission pitch:", e);
    } finally {
      setEvaluatingPitch(false);
    }
  };

  const submitObjectionResponse = async () => {
    if (!selectedObjection || !objectionResponse.trim()) return;
    setEvaluatingObjection(true);
    
    try {
      const resp = await authFetch(`${API}/formations/exercises/objections/evaluate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          collaborator_email: collaboratorEmail,
          scenario_id: selectedObjection.scenario_id,
          response_text: objectionResponse
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setObjectionResult(data.evaluation);
      }
    } catch (e) {
      console.error("Erreur soumission objection:", e);
    } finally {
      setEvaluatingObjection(false);
    }
  };

  const startSimulation = async (scenario) => {
    setSelectedScenario(scenario);
    setSimulationMessages([]);
    setSimulationResult(null);
    setSimulationActive(true);
    setSendingSimMessage(true);
    
    try {
      const resp = await authFetch(`${API}/formations/simulation/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          collaborator_email: collaboratorEmail,
          collaborator_name: collaboratorName,
          scenario_id: scenario.scenario_id,
          message: "",
          is_start: true
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setSimulationMessages([{
          role: "prospect",
          name: data.persona_name,
          content: data.response
        }]);
      }
    } catch (e) {
      console.error("Erreur démarrage simulation:", e);
    }
    setSendingSimMessage(false);
  };

  const sendSimulationMessage = async () => {
    if (!simulationInput.trim() || !selectedScenario || sendingSimMessage) return;
    
    const userMessage = simulationInput.trim();
    setSimulationInput("");
    setSimulationMessages(prev => [...prev, { role: "telepro", content: userMessage }]);
    setSendingSimMessage(true);
    
    try {
      const resp = await authFetch(`${API}/formations/simulation/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          collaborator_email: collaboratorEmail,
          collaborator_name: collaboratorName,
          scenario_id: selectedScenario.scenario_id,
          message: userMessage,
          is_start: false
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setSimulationMessages(prev => [...prev, {
          role: "prospect",
          name: data.persona_name,
          content: data.response
        }]);
        
        if (data.is_complete) {
          setSimulationActive(false);
          setSimulationResult(data.evaluation);
        }
      }
    } catch (e) {
      console.error("Erreur simulation:", e);
    }
    setSendingSimMessage(false);
  };

  const resetSimulation = () => {
    setSelectedScenario(null);
    setSimulationMessages([]);
    setSimulationResult(null);
    setSimulationActive(false);
    setSimulationMode("text");
    stopRecording();
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // MODE AUDIO - ENREGISTREMENT ET ENVOI
  // ═══════════════════════════════════════════════════════════════

  // Ref pour tracker l'état actif du mode expert (évite problèmes de closure)
  const expertModeActiveRef = useRef(false);
  const isPlayingAudioRef = useRef(false);
  const sendingSimMessageRef = useRef(false);

  // Sync refs avec les states
  useEffect(() => {
    expertModeActiveRef.current = expertModeActive;
  }, [expertModeActive]);

  useEffect(() => {
    isPlayingAudioRef.current = isPlayingAudio;
  }, [isPlayingAudio]);

  useEffect(() => {
    sendingSimMessageRef.current = sendingSimMessage;
  }, [sendingSimMessage]);

  // Mode Débutant : Push-to-Talk (appuyer pour parler)
  const startRecording = async () => {
    try {
      console.log("🎤 Démarrage enregistrement push-to-talk...");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);
      
      // Utiliser un format compatible
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType });
      
      // Reset chunks
      audioChunksRef.current = [];
      
      recorder.ondataavailable = (e) => {
        console.log("📦 Data available:", e.data.size, "bytes");
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      recorder.onstop = async () => {
        console.log("⏹️ Recorder stopped, chunks:", audioChunksRef.current.length);
        
        // Arrêter le stream
        stream.getTracks().forEach(track => track.stop());
        setAudioStream(null);
        
        if (audioChunksRef.current.length > 0) {
          const blob = new Blob(audioChunksRef.current, { type: mimeType });
          console.log("✅ Blob créé, taille:", blob.size, "bytes");
          
          if (blob.size > 500) { // Au moins 500 bytes d'audio
            await sendAudioMessage(blob);
          } else {
            console.warn("⚠️ Audio trop court, ignoré");
            setSendingSimMessage(false);
          }
        } else {
          console.error("❌ Aucun chunk audio enregistré");
          setSendingSimMessage(false);
        }
        audioChunksRef.current = [];
      };
      
      setMediaRecorder(recorder);
      recorder.start(100); // Collecter des données toutes les 100ms
      setIsRecording(true);
      console.log("🎙️ Enregistrement démarré");
    } catch (e) {
      console.error("❌ Erreur accès micro:", e);
      alert("Impossible d'accéder au microphone. Vérifiez les permissions de votre navigateur.");
    }
  };

  const stopRecording = () => {
    console.log("🛑 Arrêt enregistrement demandé...");
    
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      // Demander les dernières données avant d'arrêter
      mediaRecorder.requestData();
      // Petit délai pour s'assurer que les données sont collectées
      setTimeout(() => {
        if (mediaRecorder && mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
        }
        setIsRecording(false);
      }, 150);
    } else {
      setIsRecording(false);
    }
  };

  // Mode Expert : Conversation fluide avec détection de silence
  const startExpertMode = async () => {
    try {
      console.log("🔥 Démarrage mode expert...");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);
      audioStreamRef.current = stream; // Stocker dans la ref aussi
      
      // Créer le contexte audio pour analyser le niveau sonore
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyserNode = ctx.createAnalyser();
      analyserNode.fftSize = 256;
      analyserNode.smoothingTimeConstant = 0.5;
      
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyserNode);
      
      setAudioContext(ctx);
      setAnalyser(analyserNode);
      
      // Créer le premier recorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      recorder.onstop = async () => {
        console.log("🔥 Expert initial: recorder stopped, chunks:", audioChunksRef.current.length);
        const chunksToSend = [...audioChunksRef.current];
        audioChunksRef.current = [];
        
        if (chunksToSend.length > 0) {
          const blob = new Blob(chunksToSend, { type: mimeType });
          console.log("🔥 Expert initial: blob size:", blob.size);
          if (blob.size > 1000) {
            await sendAudioMessage(blob);
          } else {
            setSendingSimMessage(false);
            sendingSimMessageRef.current = false;
          }
        }
        // Note: le redémarrage se fait via playAudioResponse.onended
      };
      
      recorder.start(100);
      recorderStartTimeRef.current = Date.now(); // Timestamp de démarrage
      setMediaRecorder(recorder);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setExpertModeActive(true);
      expertModeActiveRef.current = true;
      
      console.log("🔥 Expert: recorder initial démarré");
      
      // Démarrer la détection de voix
      detectVoiceActivity(analyserNode, stream);
      
    } catch (e) {
      console.error("❌ Erreur mode expert:", e);
      alert("Impossible d'accéder au microphone. Vérifiez les permissions.");
    }
  };

  const detectVoiceActivity = (analyserNode, stream) => {
    const bufferLength = analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    let speakingStartTime = null;
    let silenceStartTime = null;
    const SILENCE_THRESHOLD = 10;
    const SILENCE_DURATION = 1500;
    const MIN_SPEAKING_DURATION = 300;
    const MIN_RECORDING_TIME = 500; // Minimum 500ms d'enregistrement avant d'envoyer
    
    const checkAudio = () => {
      if (!expertModeActiveRef.current || !stream.active) {
        console.log("🔥 VAD: arrêt");
        return;
      }
      
      // Reset VAD si demandé (après restart du recorder)
      if (vadResetRef.current) {
        console.log("🔥 VAD: RESET des variables de timing");
        speakingStartTime = null;
        silenceStartTime = null;
        vadResetRef.current = false;
      }
      
      analyserNode.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
      
      if (average > SILENCE_THRESHOLD) {
        if (!speakingStartTime) {
          speakingStartTime = Date.now();
          console.log("🗣️ VAD: parole détectée, niveau:", average.toFixed(1));
        }
        silenceStartTime = null;
        setIsSpeaking(true);
      } else {
        if (speakingStartTime && !silenceStartTime) {
          silenceStartTime = Date.now();
        }
        setIsSpeaking(false);
        
        if (silenceStartTime && speakingStartTime) {
          const speakingDuration = silenceStartTime - speakingStartTime;
          const silenceDuration = Date.now() - silenceStartTime;
          
          if (speakingDuration > MIN_SPEAKING_DURATION && silenceDuration > SILENCE_DURATION) {
            const canSend = !sendingSimMessageRef.current && !isPlayingAudioRef.current;
            const currentRecorder = mediaRecorderRef.current;
            const recorderReady = currentRecorder && currentRecorder.state === 'recording';
            
            // Vérifier que le recorder a eu le temps d'enregistrer
            const recorderAge = recorderStartTimeRef.current ? (Date.now() - recorderStartTimeRef.current) : 0;
            const hasEnoughRecording = recorderAge > MIN_RECORDING_TIME && audioChunksRef.current.length > 0;
            
            console.log("🔥 VAD check: canSend=", canSend, "recorderReady=", recorderReady, 
                        "recorderAge=", recorderAge, "chunks=", audioChunksRef.current.length,
                        "hasEnough=", hasEnoughRecording);
            
            if (canSend && recorderReady && hasEnoughRecording) {
              console.log("🔥 VAD: ENVOI après", speakingDuration, "ms de parole");
              setSendingSimMessage(true);
              sendingSimMessageRef.current = true;
              currentRecorder.stop();
              speakingStartTime = null;
              silenceStartTime = null;
            } else if (!canSend) {
              console.log("🔥 VAD: en attente (sending ou playing)");
            } else if (!hasEnoughRecording) {
              console.log("🔥 VAD: pas assez d'enregistrement, reset");
              speakingStartTime = null;
              silenceStartTime = null;
            } else if (!recorderReady) {
              console.log("🔥 VAD: recorder pas prêt, reset");
              speakingStartTime = null;
              silenceStartTime = null;
            }
          }
        }
      }
      
      if (expertModeActiveRef.current && stream.active) {
        requestAnimationFrame(checkAudio);
      }
    };
    
    requestAnimationFrame(checkAudio);
  };

  const stopExpertMode = () => {
    console.log("🛑 Arrêt mode expert...");
    
    // Désactiver d'abord via la ref pour stopper la boucle VAD
    expertModeActiveRef.current = false;
    setExpertModeActive(false);
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        console.log("Recorder déjà arrêté");
      }
    }
    mediaRecorderRef.current = null;
    setMediaRecorder(null);
    setIsRecording(false);
    
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    setAudioStream(null);
    
    if (audioContext && audioContext.state !== 'closed') {
      audioContext.close();
      setAudioContext(null);
    }
    setAnalyser(null);
    setIsSpeaking(false);
    audioChunksRef.current = [];
  };

  const sendAudioMessage = async (audioBlob) => {
    if (!selectedScenario) {
      console.error("❌ Pas de scénario sélectionné");
      setSendingSimMessage(false);
      return;
    }
    
    // Ne pas bloquer si déjà en cours - c'est normal car stopRecording l'a mis à true
    console.log("📤 Envoi audio, taille:", audioBlob.size, "bytes");
    setSendingSimMessage(true);
    
    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.webm');
    formData.append('collaborator_email', collaboratorEmail);
    formData.append('collaborator_name', collaboratorName);
    formData.append('scenario_id', selectedScenario.scenario_id);
    formData.append('is_start', 'false');
    
    try {
      console.log("📡 Appel API /formations/simulation/audio...");
      const resp = await authFetch(`${API}/formations/simulation/audio`, {
        method: "POST",
        body: formData
      });
      
      if (resp.ok) {
        const data = await resp.json();
        console.log("✅ Réponse reçue:", data.response?.substring(0, 50));
        
        // Ajouter le message utilisateur transcrit
        if (data.user_text) {
          setSimulationMessages(prev => [...prev, { 
            role: "telepro", 
            content: data.user_text,
            isTranscription: true
          }]);
        }
        
        // Ajouter la réponse du prospect
        setSimulationMessages(prev => [...prev, {
          role: "prospect",
          name: data.persona_name,
          content: data.response,
          hasAudio: true
        }]);
        
        // Reset sending state BEFORE playing audio (so restart can happen after audio ends)
        setSendingSimMessage(false);
        sendingSimMessageRef.current = false;
        
        // Jouer l'audio de la réponse
        if (data.response_audio_base64) {
          playAudioResponse(data.response_audio_base64);
        }
        
        if (data.is_complete) {
          setSimulationActive(false);
          setSimulationResult(data.evaluation);
          // Arrêter le mode expert si la simulation est terminée
          if (expertModeActiveRef.current) {
            stopExpertMode();
          }
        }
      }
    } catch (e) {
      console.error("Erreur envoi audio:", e);
      setSendingSimMessage(false);
      sendingSimMessageRef.current = false;
    }
  };

  const startAudioSimulation = async (scenario) => {
    setSelectedScenario(scenario);
    setSimulationMessages([]);
    setSimulationResult(null);
    setSimulationActive(true);
    setSimulationMode("audio");
    setSendingSimMessage(true);
    
    const formData = new FormData();
    formData.append('file', new Blob([''], { type: 'audio/webm' }), 'empty.webm');
    formData.append('collaborator_email', collaboratorEmail);
    formData.append('collaborator_name', collaboratorName);
    formData.append('scenario_id', scenario.scenario_id);
    formData.append('is_start', 'true');
    
    try {
      const resp = await authFetch(`${API}/formations/simulation/audio`, {
        method: "POST",
        body: formData
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setSimulationMessages([{
          role: "prospect",
          name: data.persona_name,
          content: data.response,
          hasAudio: true
        }]);
        
        // Jouer l'audio de bienvenue
        if (data.response_audio_base64) {
          playAudioResponse(data.response_audio_base64);
        }
      }
    } catch (e) {
      console.error("Erreur démarrage simulation audio:", e);
    }
    setSendingSimMessage(false);
  };

  const playAudioResponse = (base64Audio) => {
    try {
      // Arrêter l'audio précédent si en cours
      if (currentAudio) {
        currentAudio.pause();
      }
      
      const audioSrc = `data:audio/mp3;base64,${base64Audio}`;
      const audio = new Audio(audioSrc);
      
      audio.onplay = () => {
        console.log("🔊 Audio Fatima: lecture démarrée");
        setIsPlayingAudio(true);
        isPlayingAudioRef.current = true;
      };
      
      audio.onended = () => {
        console.log("🔊 Audio Fatima: lecture terminée");
        setIsPlayingAudio(false);
        isPlayingAudioRef.current = false;
        
        // Redémarrer le recorder en mode Expert si nécessaire
        console.log("🔊 Checking restart: expertMode=", expertModeActiveRef.current, "streamRef=", !!audioStreamRef.current);
        if (expertModeActiveRef.current) {
          console.log("🔥 Expert: redémarrage recorder après audio Fatima");
          restartExpertRecorder();
        }
      };
      
      audio.onerror = () => {
        console.error("🔊 Audio Fatima: erreur");
        setIsPlayingAudio(false);
        isPlayingAudioRef.current = false;
      };
      
      setCurrentAudio(audio);
      audio.play();
    } catch (e) {
      console.error("Erreur lecture audio:", e);
    }
  };

  // Fonction pour redémarrer le recorder en mode Expert
  const restartExpertRecorder = () => {
    console.log("🔥 restartExpertRecorder called");
    
    const stream = audioStreamRef.current;
    
    if (!stream || !stream.active) {
      console.log("🔥 Cannot restart: stream inactive");
      return;
    }
    
    if (!expertModeActiveRef.current) {
      console.log("🔥 Cannot restart: expert mode off");
      return;
    }
    
    if (sendingSimMessageRef.current) {
      console.log("🔥 Cannot restart: still sending, will retry in 500ms");
      setTimeout(restartExpertRecorder, 500);
      return;
    }
    
    if (isPlayingAudioRef.current) {
      console.log("🔥 Cannot restart: audio playing");
      return;
    }
    
    // Arrêter l'ancien recorder si existant
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log("🔥 Stopping old recorder first");
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {}
    }
    
    // IMPORTANT: Reset les variables VAD pour éviter l'envoi immédiat avec les anciens timings
    vadResetRef.current = true;
    audioChunksRef.current = [];
    
    const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
    const recorder = new MediaRecorder(stream, { mimeType });
    
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        audioChunksRef.current.push(e.data);
      }
    };
    
    recorder.onstop = async () => {
      console.log("🔥 Recorder stopped, chunks:", audioChunksRef.current.length);
      const chunksToSend = [...audioChunksRef.current];
      audioChunksRef.current = [];
      
      if (chunksToSend.length > 0) {
        const blob = new Blob(chunksToSend, { type: mimeType });
        console.log("🔥 Blob size:", blob.size);
        if (blob.size > 1000) {
          await sendAudioMessage(blob);
        } else {
          console.log("🔥 Blob too small, skipping");
          setSendingSimMessage(false);
          sendingSimMessageRef.current = false;
        }
      } else {
        console.log("🔥 No chunks, skipping");
        setSendingSimMessage(false);
        sendingSimMessageRef.current = false;
      }
    };
    
    recorder.start(100);
    recorderStartTimeRef.current = Date.now(); // Enregistrer le timestamp de démarrage
    setMediaRecorder(recorder);
    mediaRecorderRef.current = recorder;
    console.log("🔥 Nouveau recorder démarré, state:", recorder.state);
  };

  const stopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      setIsPlayingAudio(false);
    }
  };

  // Scroll simulation messages
  useEffect(() => {
    simEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [simulationMessages]);

  // Calculer les stats de progression
  const completedSteps = progress?.stats?.total_steps_completed || 0;
  const totalSteps = modules.reduce((acc, m) => acc + (m.steps?.length || 0), 0);
  const progressPercent = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  const badges = progress?.badges || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Formations & Compétences</h1>
                  <p className="text-sm text-slate-400">
                    {isManager ? "Vue Manager - Gérez les formations de l'équipe" : `Bienvenue ${collaboratorName}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Onglets */}
            <div className="flex gap-2 flex-wrap">
              {["overview", "modules", "training", "quiz", "simulation", "exercises", "documents", "calculateur", "analytics"].map(tab => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    if (tab === "quiz" && !quizData) {
                      loadQuiz("mpa_1_qualification");
                    } else if (tab === "simulation" && simulationScenarios.length === 0) {
                      loadSimulations();
                    } else if (tab === "documents" && documents.length === 0) {
                      loadDocuments();
                    } else if (tab === "exercises" && objectionScenarios.length === 0) {
                      loadObjectionScenarios();
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? "bg-purple-600 text-white"
                      : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                  }`}
                >
                  {tab === "overview" && "Vue d'ensemble"}
                  {tab === "modules" && "Modules"}
                  {tab === "training" && "Formation"}
                  {tab === "quiz" && "🎯 Quiz"}
                  {tab === "simulation" && "🎭 Simulation"}
                  {tab === "exercises" && "💪 Exercices"}
                  {tab === "documents" && "📄 Documents"}
                  {tab === "calculateur" && "🧮 Calculateur"}
                  {tab === "analytics" && "Analyse"}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* VUE D'ENSEMBLE */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-slate-400">Progression</span>
                </div>
                <p className="text-3xl font-bold">{progressPercent}%</p>
                <div className="w-full bg-slate-700 rounded-full h-2 mt-2">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-slate-400">Étapes validées</span>
                </div>
                <p className="text-3xl font-bold">{completedSteps}<span className="text-lg text-slate-500">/{totalSteps}</span></p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-amber-400" />
                  </div>
                  <span className="text-slate-400">Badges obtenus</span>
                </div>
                <p className="text-3xl font-bold">{badges.length}</p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-slate-400">Modules disponibles</span>
                </div>
                <p className="text-3xl font-bold">{modules.length}</p>
              </div>
            </div>

            {/* Badges */}
            {badges.length > 0 && (
              <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-5">
                <h3 className="font-semibold text-amber-400 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5" /> Mes badges
                </h3>
                <div className="flex flex-wrap gap-3">
                  {badges.map((badge, i) => (
                    <div key={i} className="flex items-center gap-2 bg-amber-500/20 rounded-lg px-4 py-2">
                      <Star className="w-4 h-4 text-amber-400" />
                      <span className="text-sm font-medium">{badge.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions rapides */}
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setActiveTab("modules")}
                className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-6 text-left hover:border-purple-400 transition-colors group"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Commencer une formation</h3>
                    <p className="text-sm text-slate-400">Parcourir les modules disponibles</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-purple-400 ml-auto" />
              </button>

              <button
                onClick={analyzeMyalls}
                disabled={analyzingCalls}
                className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-xl p-6 text-left hover:border-blue-400 transition-colors group disabled:opacity-50"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    {analyzingCalls ? <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /> : <Phone className="w-6 h-6 text-blue-400" />}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Analyser mes appels</h3>
                    <p className="text-sm text-slate-400">Fatima analyse mes derniers appels</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-blue-400 ml-auto" />
              </button>
            </div>

            {/* Résultat analyse appels */}
            {callAnalysis && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-blue-400 flex items-center gap-2">
                    <Brain className="w-5 h-5" /> Analyse de Fatima ({callAnalysis.calls_analyzed} appels)
                  </h3>
                  <button onClick={() => setCallAnalysis(null)} className="p-1 hover:bg-slate-700 rounded">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="prose prose-invert prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-slate-300">{callAnalysis.analysis}</div>
                </div>
              </div>
            )}

            {/* Formations recommandées */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" /> Formations recommandées
              </h3>
              <div className="space-y-3">
                {modules.filter(m => m.type === "onboarding").slice(0, 3).map((module, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium">{module.title}</p>
                        <p className="text-sm text-slate-400">{module.duration_hours}h • {module.steps?.length || 0} étapes</p>
                      </div>
                    </div>
                    <button
                      onClick={() => startModule(module)}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm flex items-center gap-2"
                    >
                      <Play className="w-4 h-4" /> Commencer
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* MODULES */}
        {activeTab === "modules" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Programme MaPrimeAdapt' - 4 modules (4h)</h2>
              <button
                onClick={() => requestFormation()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Demander une formation
              </button>
            </div>

            {/* Banner programme */}
            <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-xl p-5 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-3xl">
                  🎯
                </div>
                <div>
                  <h3 className="text-lg font-bold">Formation Téléprospection MaPrimeAdapt'</h3>
                  <p className="text-slate-400">4 modules × 60 min • Quiz interactifs • Simulations avec Fatima IA</p>
                  <div className="flex gap-3 mt-2">
                    <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">Certification disponible</span>
                    <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded">Badges Bronze/Argent/Or</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {modules.map((module, i) => {
                const moduleProgress = progress?.progress?.filter(p => p.module_id === module.module_id) || [];
                const completedInModule = moduleProgress.filter(p => p.status === "completed").length;
                const totalInModule = module.steps?.length || 0;
                const modulePercent = totalInModule > 0 ? Math.round((completedInModule / totalInModule) * 100) : 0;
                const isCompleted = modulePercent === 100;

                return (
                  <div
                    key={i}
                    className={`bg-slate-800/50 border rounded-xl p-5 ${
                      isCompleted ? "border-emerald-500/50" : "border-slate-700/50"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                          isCompleted ? "bg-emerald-500/20" : "bg-purple-500/20"
                        }`}>
                          {module.icon || (isCompleted ? "✅" : "📚")}
                        </div>
                        <div>
                          <h3 className="font-bold">{module.title}</h3>
                          <p className="text-sm text-slate-400">
                            {module.duration_minutes ? `${module.duration_minutes} min` : `${module.duration_hours}h`} • {totalInModule} étapes
                          </p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        module.category === "maprimeadapt" ? "bg-blue-500/20 text-blue-400" :
                        module.type === "onboarding" ? "bg-purple-500/20 text-purple-400" :
                        "bg-orange-500/20 text-orange-400"
                      }`}>
                        {module.category === "maprimeadapt" ? "MaPrimeAdapt'" :
                         module.type === "onboarding" ? "Onboarding" : "Continue"}
                      </span>
                    </div>

                    <p className="text-sm text-slate-400 mb-4">{module.description}</p>

                    {/* Objectifs du module */}
                    {module.objectives && module.objectives.length > 0 && (
                      <div className="mb-4 bg-slate-900/50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-2 font-medium">🎯 Objectifs :</p>
                        <ul className="space-y-1">
                          {module.objectives.slice(0, 3).map((obj, j) => (
                            <li key={j} className="text-xs text-slate-400 flex items-start gap-2">
                              <span className="text-emerald-400 mt-0.5">✓</span>
                              {obj}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Compétences */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {module.skills?.map((skill, j) => (
                        <span key={j} className="px-2 py-1 bg-slate-700/50 rounded text-xs text-slate-300">
                          {skill}
                        </span>
                      ))}
                    </div>

                    {/* Badges disponibles */}
                    {module.badge && (
                      <div className="flex gap-2 mb-4">
                        <span className="text-xs bg-orange-500/10 text-orange-400 px-2 py-1 rounded" title={`Score ≥ ${module.badge.bronze?.threshold}%`}>
                          🥉 {module.badge.bronze?.name || 'Bronze'}
                        </span>
                        <span className="text-xs bg-slate-500/10 text-slate-300 px-2 py-1 rounded" title={`Score ≥ ${module.badge.silver?.threshold}%`}>
                          🥈 {module.badge.silver?.name || 'Argent'}
                        </span>
                        <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-1 rounded" title={`Score ≥ ${module.badge.gold?.threshold}%`}>
                          🥇 {module.badge.gold?.name || 'Or'}
                        </span>
                      </div>
                    )}

                    {/* Progression */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-400">Progression</span>
                        <span className={isCompleted ? "text-emerald-400" : "text-slate-300"}>{modulePercent}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            isCompleted ? "bg-emerald-500" : "bg-purple-500"
                          }`}
                          style={{ width: `${modulePercent}%` }}
                        />
                      </div>
                    </div>

                    <button
                      onClick={() => startModule(module)}
                      className={`w-full py-2 rounded-lg text-sm flex items-center justify-center gap-2 ${
                        isCompleted
                          ? "bg-slate-700 text-slate-300 hover:bg-slate-600"
                          : "bg-purple-600 hover:bg-purple-700 text-white"
                      }`}
                    >
                      {isCompleted ? (
                        <>Revoir le module</>
                      ) : modulePercent > 0 ? (
                        <><Play className="w-4 h-4" /> Continuer</>
                      ) : (
                        <><Play className="w-4 h-4" /> Commencer</>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* SESSION DE FORMATION */}
        {activeTab === "training" && (
          <div className="grid grid-cols-3 gap-6 h-[calc(100vh-200px)]">
            {/* Liste des étapes */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 overflow-y-auto">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-purple-400" />
                {selectedModule?.title || "Sélectionnez un module"}
              </h3>

              {selectedModule ? (
                <div className="space-y-2">
                  {selectedModule.steps?.map((step, i) => {
                    const stepProgress = progress?.progress?.find(
                      p => p.module_id === selectedModule.module_id && p.step_id === step.step_id
                    );
                    const isCompleted = stepProgress?.status === "completed";
                    const isCurrent = selectedStep?.step_id === step.step_id;

                    return (
                      <button
                        key={i}
                        onClick={() => {
                          setSelectedStep(step);
                          setChatMessages([{
                            role: "assistant",
                            content: `Passons à l'étape : **${step.title}**\n\n${step.content || "C'est parti !"}\n\nDes questions ?`
                          }]);
                        }}
                        className={`w-full text-left p-3 rounded-lg flex items-center gap-3 transition-colors ${
                          isCurrent
                            ? "bg-purple-500/20 border border-purple-500/50"
                            : isCompleted
                            ? "bg-emerald-500/10 border border-emerald-500/30"
                            : "bg-slate-900/50 border border-slate-700/50 hover:bg-slate-800"
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isCompleted ? "bg-emerald-500" : isCurrent ? "bg-purple-500" : "bg-slate-700"
                        }`}>
                          {isCompleted ? (
                            <CheckCircle className="w-4 h-4 text-white" />
                          ) : (
                            <span className="text-sm font-bold">{i + 1}</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{step.title}</p>
                          <p className="text-xs text-slate-500">{step.type}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-slate-500 py-8">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Sélectionnez un module dans l'onglet "Modules"</p>
                </div>
              )}
            </div>

            {/* Zone de chat avec Fatima */}
            <div className="col-span-2 bg-slate-800/50 border border-slate-700/50 rounded-xl flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center">
                    🎯
                  </div>
                  <div>
                    <h3 className="font-bold">Fatima Benali</h3>
                    <p className="text-xs text-slate-400">Formatrice Pôle Télépro</p>
                  </div>
                </div>
                {selectedStep && (
                  <button
                    onClick={completeStep}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" /> Valider cette étape
                  </button>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {chatMessages.length === 0 ? (
                  <div className="text-center text-slate-500 py-12">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Sélectionnez un module pour commencer la formation</p>
                  </div>
                ) : (
                  chatMessages.map((msg, i) => (
                    <div
                      key={i}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[90%] rounded-2xl p-4 ${
                          msg.role === "user"
                            ? "bg-purple-600 text-white"
                            : "bg-slate-700/50 text-slate-200"
                        }`}
                      >
                        <div 
                          className="whitespace-pre-wrap text-sm prose prose-invert prose-sm max-w-none
                            prose-headings:text-white prose-headings:font-bold prose-headings:mt-4 prose-headings:mb-2
                            prose-p:my-2 prose-ul:my-2 prose-li:my-1
                            prose-strong:text-emerald-300 prose-strong:font-semibold
                            prose-table:border-collapse prose-table:w-full
                            prose-th:border prose-th:border-slate-600 prose-th:bg-slate-800 prose-th:p-2 prose-th:text-left
                            prose-td:border prose-td:border-slate-600 prose-td:p-2"
                          dangerouslySetInnerHTML={{
                            __html: msg.content
                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                              .replace(/^### (.*$)/gm, '<h3>$1</h3>')
                              .replace(/^## (.*$)/gm, '<h2>$1</h2>')
                              .replace(/^# (.*$)/gm, '<h1>$1</h1>')
                              .replace(/^> (.*$)/gm, '<blockquote class="border-l-4 border-purple-500 pl-3 my-2 text-slate-300 italic">$1</blockquote>')
                              .replace(/^- (.*$)/gm, '<li class="list-disc ml-4">$1</li>')
                              .replace(/^(\d+)\. (.*$)/gm, '<li class="list-decimal ml-4">$2</li>')
                              .replace(/\n/g, '<br/>')
                              .replace(/```([\s\S]*?)```/g, '<pre class="bg-slate-900 p-3 rounded-lg text-xs overflow-x-auto my-2"><code>$1</code></pre>')
                              .replace(/\|(.+)\|/g, (match) => {
                                const cells = match.split('|').filter(c => c.trim());
                                const isHeader = match.includes('---');
                                if (isHeader) return '';
                                return `<tr>${cells.map(c => `<td class="border border-slate-600 px-3 py-2">${c.trim()}</td>`).join('')}</tr>`;
                              })
                          }}
                        />
                      </div>
                    </div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-slate-700/50">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && sendTrainingMessage()}
                    placeholder="Posez une question à Fatima..."
                    className="flex-1 bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500"
                    disabled={!selectedModule || sendingMessage}
                  />
                  <button
                    onClick={sendTrainingMessage}
                    disabled={!chatInput.trim() || !selectedModule || sendingMessage}
                    className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-xl flex items-center gap-2"
                  >
                    {sendingMessage ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* QUIZ INTERACTIF */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {activeTab === "quiz" && (
          <div className="space-y-6">
            {!quizResult ? (
              <>
                {/* Header Quiz */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-3">
                      <Target className="w-6 h-6 text-amber-400" />
                      Quiz Éligibilité MaPrimeAdapt'
                    </h2>
                    <p className="text-slate-400 mt-1">
                      Évaluez 10 profils prospects - Objectif : 8/10 en moins de 3 minutes
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="bg-slate-800 px-4 py-2 rounded-lg flex items-center gap-2">
                      <Timer className="w-4 h-4 text-blue-400" />
                      <span className="font-mono">
                        {quizStartTime ? Math.floor((Date.now() - quizStartTime) / 1000) : 0}s
                      </span>
                    </div>
                    <div className="bg-slate-800 px-4 py-2 rounded-lg">
                      <span className="text-emerald-400 font-bold">{Object.keys(quizAnswers).length}</span>
                      <span className="text-slate-400">/{quizData?.profiles?.length || 10}</span>
                    </div>
                  </div>
                </div>

                {/* Profils à évaluer */}
                {quizData?.profiles && (
                  <div className="grid grid-cols-2 gap-4">
                    {quizData.profiles.map((profil, index) => {
                      const isAnswered = quizAnswers[profil.id] !== undefined;
                      const isCurrent = index === currentQuizIndex;
                      
                      return (
                        <div
                          key={profil.id}
                          className={`bg-slate-800/50 border rounded-xl p-5 transition-all ${
                            isCurrent ? "border-amber-500/50 ring-2 ring-amber-500/20" :
                            isAnswered ? "border-emerald-500/30 opacity-75" : "border-slate-700/50"
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <span className="bg-slate-700 px-2 py-1 rounded text-xs text-slate-400">
                              Profil #{profil.id}
                            </span>
                            {isAnswered && (
                              <CheckCircle className="w-5 h-5 text-emerald-400" />
                            )}
                          </div>
                          
                          <p className="text-sm mb-4 leading-relaxed">{profil.profil}</p>
                          
                          <div className="flex gap-3">
                            <button
                              onClick={() => submitQuizAnswer(profil.id, true)}
                              disabled={isAnswered}
                              className={`flex-1 py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                                quizAnswers[profil.id] === true
                                  ? "bg-emerald-600 text-white"
                                  : "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 disabled:opacity-50"
                              }`}
                              data-testid={`quiz-eligible-${profil.id}`}
                            >
                              <ThumbsUp className="w-4 h-4" /> Éligible
                            </button>
                            <button
                              onClick={() => submitQuizAnswer(profil.id, false)}
                              disabled={isAnswered}
                              className={`flex-1 py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                                quizAnswers[profil.id] === false
                                  ? "bg-red-600 text-white"
                                  : "bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:opacity-50"
                              }`}
                              data-testid={`quiz-non-eligible-${profil.id}`}
                            >
                              <ThumbsDown className="w-4 h-4" /> Non éligible
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Bouton de soumission */}
                <div className="flex justify-center">
                  <button
                    onClick={submitQuiz}
                    disabled={Object.keys(quizAnswers).length < (quizData?.profiles?.length || 10) || submittingQuiz}
                    className="px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 disabled:opacity-50 rounded-xl font-medium flex items-center gap-3 transition-all"
                    data-testid="submit-quiz-btn"
                  >
                    {submittingQuiz ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" /> Évaluation...
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5" /> Valider mes réponses
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              /* Résultats du Quiz */
              <div className="max-w-3xl mx-auto space-y-6">
                {/* Score global */}
                <div className={`text-center p-8 rounded-2xl ${
                  quizResult.passed 
                    ? "bg-gradient-to-br from-emerald-900/50 to-teal-900/50 border border-emerald-500/30"
                    : "bg-gradient-to-br from-orange-900/50 to-red-900/50 border border-orange-500/30"
                }`}>
                  <div className="text-6xl mb-4">
                    {quizResult.badge?.level === "gold" ? "🥇" :
                     quizResult.badge?.level === "silver" ? "🥈" :
                     quizResult.badge?.level === "bronze" ? "🥉" : "📊"}
                  </div>
                  <h2 className="text-3xl font-bold mb-2">{quizResult.score}%</h2>
                  <p className="text-lg text-slate-300">
                    {quizResult.correct_count} / {quizResult.total_questions} bonnes réponses
                  </p>
                  <p className="text-slate-400 mt-2">
                    Temps : {quizResult.time_spent_seconds} secondes
                  </p>
                  {quizResult.badge && (
                    <div className="mt-4 inline-block bg-slate-800/50 px-4 py-2 rounded-full">
                      <span className="text-amber-400 font-medium">
                        {quizResult.badge.name}
                      </span>
                    </div>
                  )}
                  <p className="mt-4 text-lg">{quizResult.feedback}</p>
                </div>

                {/* Détail des réponses */}
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <h3 className="font-semibold mb-4">Détail des réponses</h3>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {quizResult.results?.map((result, i) => (
                      <div
                        key={i}
                        className={`p-4 rounded-lg border ${
                          result.is_correct
                            ? "bg-emerald-500/10 border-emerald-500/30"
                            : "bg-red-500/10 border-red-500/30"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-xs text-slate-400">Profil #{result.question_id}</span>
                          {result.is_correct ? (
                            <CheckCircle className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <X className="w-5 h-5 text-red-400" />
                          )}
                        </div>
                        <p className="text-sm mb-2">{result.profil}</p>
                        <div className="text-xs">
                          <span className={result.is_correct ? "text-emerald-400" : "text-red-400"}>
                            Votre réponse : {result.user_answer}
                          </span>
                          {!result.is_correct && (
                            <span className="text-slate-400 ml-2">
                              (Correct : {result.correct_answer})
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-2 italic">{result.explication}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Boutons */}
                <div className="flex justify-center gap-4">
                  <button
                    onClick={resetQuiz}
                    className="px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl flex items-center gap-2"
                  >
                    <ArrowLeft className="w-5 h-5" /> Retour aux modules
                  </button>
                  <button
                    onClick={() => loadQuiz("mpa_1_qualification")}
                    className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-xl flex items-center gap-2"
                  >
                    <RefreshCw className="w-5 h-5" /> Recommencer le quiz
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* SIMULATION AVEC FATIMA */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {activeTab === "simulation" && (
          <div className="space-y-6">
            {!selectedScenario ? (
              <>
                {/* Liste des scénarios */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-3">
                      <Phone className="w-6 h-6 text-blue-400" />
                      Simulations d'appels avec Fatima
                    </h2>
                    <p className="text-slate-400 mt-1">
                      Entraînez-vous avec des prospects virtuels réalistes
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {simulationScenarios.map((scenario, i) => (
                    <div
                      key={i}
                      className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 hover:border-blue-500/30 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              scenario.difficulty === "easy" ? "bg-emerald-500/20 text-emerald-400" :
                              scenario.difficulty === "medium" ? "bg-amber-500/20 text-amber-400" :
                              "bg-red-500/20 text-red-400"
                            }`}>
                              {scenario.difficulty === "easy" ? "Facile" :
                               scenario.difficulty === "medium" ? "Moyen" : "Difficile"}
                            </span>
                            <h3 className="font-semibold">{scenario.title}</h3>
                          </div>
                          
                          <div className="bg-slate-900/50 rounded-lg p-3 mb-3">
                            <div className="text-sm">
                              <p><strong className="text-blue-400">{scenario.persona.name}</strong>, {scenario.persona.age} ans - {scenario.persona.city}</p>
                              <p className="text-slate-400 mt-1">{scenario.persona.situation}</p>
                              <p className="text-xs text-slate-500 mt-2 italic">Personnalité : {scenario.persona.personality}</p>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap gap-2">
                            {scenario.key_moments?.map((moment, j) => (
                              <span key={j} className="px-2 py-1 bg-slate-700/50 rounded text-xs text-slate-400">
                                {moment.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        <div className="ml-4 flex flex-col gap-2">
                          <button
                            onClick={() => startSimulation(scenario)}
                            className="px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl flex items-center gap-2"
                            data-testid={`start-sim-text-${scenario.scenario_id}`}
                          >
                            <MessageSquare className="w-4 h-4" /> Mode Texte
                          </button>
                          <button
                            onClick={() => startAudioSimulation(scenario)}
                            className="px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-xl flex items-center gap-2"
                            data-testid={`start-sim-audio-${scenario.scenario_id}`}
                          >
                            <Mic className="w-4 h-4" /> Mode Audio 🎤
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              /* Session de simulation active */
              <div className="grid grid-cols-3 gap-6 h-[calc(100vh-200px)]">
                {/* Info prospect */}
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-400" />
                      Fiche Prospect
                    </h3>
                    <span className={`px-2 py-1 rounded text-xs ${
                      selectedScenario.difficulty === "easy" ? "bg-emerald-500/20 text-emerald-400" :
                      selectedScenario.difficulty === "medium" ? "bg-amber-500/20 text-amber-400" :
                      "bg-red-500/20 text-red-400"
                    }`}>
                      {selectedScenario.difficulty === "easy" ? "Facile" :
                       selectedScenario.difficulty === "medium" ? "Moyen" : "Difficile"}
                    </span>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="text-center p-4 bg-gradient-to-br from-blue-900/30 to-purple-900/30 rounded-xl">
                      <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                        <User className="w-8 h-8 text-blue-400" />
                      </div>
                      <h4 className="font-bold text-lg">{selectedScenario.persona.name}</h4>
                      <p className="text-slate-400">{selectedScenario.persona.age} ans</p>
                      <p className="text-slate-400">{selectedScenario.persona.city}</p>
                    </div>
                    
                    <div>
                      <h5 className="text-xs text-slate-500 uppercase mb-2">Situation</h5>
                      <p className="text-sm text-slate-300">{selectedScenario.persona.situation}</p>
                    </div>
                    
                    <div>
                      <h5 className="text-xs text-slate-500 uppercase mb-2">Personnalité</h5>
                      <p className="text-sm text-slate-400 italic">{selectedScenario.persona.personality}</p>
                    </div>
                    
                    <div>
                      <h5 className="text-xs text-slate-500 uppercase mb-2">Objectif</h5>
                      <p className="text-sm text-emerald-400">
                        {selectedScenario.expected_outcome === "rdv_fixe" ? "🎯 Fixer un RDV" :
                         selectedScenario.expected_outcome === "rdv_apres_objection" ? "🎯 RDV après objection" :
                         selectedScenario.expected_outcome === "disqualification_polie" ? "❌ Disqualifier poliment" :
                         "🎯 " + selectedScenario.expected_outcome}
                      </p>
                    </div>
                    
                    {!simulationActive && simulationResult && (
                      <button
                        onClick={resetSimulation}
                        className="w-full py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                      >
                        ← Retour aux scénarios
                      </button>
                    )}
                  </div>
                </div>

                {/* Zone de conversation */}
                <div className="col-span-2 bg-slate-800/50 border border-slate-700/50 rounded-xl flex flex-col">
                  {/* Header */}
                  <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                        📞
                      </div>
                      <div>
                        <h3 className="font-bold">Appel en cours</h3>
                        <p className="text-xs text-slate-400">
                          {selectedScenario.title}
                        </p>
                      </div>
                    </div>
                    {simulationActive && (
                      <span className="flex items-center gap-2 text-emerald-400 text-sm">
                        <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                        En ligne
                      </span>
                    )}
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {simulationMessages.map((msg, i) => (
                      <div
                        key={i}
                        className={`flex ${msg.role === "telepro" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-2xl p-4 ${
                            msg.role === "telepro"
                              ? "bg-purple-600 text-white"
                              : "bg-slate-700/50 text-slate-200"
                          }`}
                        >
                          {msg.role === "prospect" && (
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-blue-400 font-medium">{msg.name}</p>
                              {msg.hasAudio && (
                                <span className="text-xs text-purple-400 flex items-center gap-1">
                                  <Volume2 className="w-3 h-3" /> Audio
                                </span>
                              )}
                            </div>
                          )}
                          {msg.isTranscription && (
                            <p className="text-xs text-purple-300/70 mb-1 flex items-center gap-1">
                              <Mic className="w-3 h-3" /> Transcription
                            </p>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    ))}
                    
                    {sendingSimMessage && (
                      <div className="flex justify-start">
                        <div className="bg-slate-700/50 rounded-2xl p-4">
                          <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                        </div>
                      </div>
                    )}
                    
                    <div ref={simEndRef} />
                  </div>

                  {/* Résultat de l'évaluation */}
                  {simulationResult && (
                    <div className="p-4 bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-t border-amber-500/30">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold flex items-center gap-2">
                          <Trophy className="w-5 h-5 text-amber-400" />
                          Évaluation de Fatima
                        </h4>
                        <span className="text-2xl font-bold text-amber-400">
                          {simulationResult.total || 0}/20
                        </span>
                      </div>
                      <p className="text-sm text-slate-300 mb-3">{simulationResult.feedback}</p>
                      {simulationResult.axes_amelioration?.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 mb-2">Axes d'amélioration :</p>
                          <div className="flex flex-wrap gap-2">
                            {simulationResult.axes_amelioration.map((axe, i) => (
                              <span key={i} className="px-2 py-1 bg-slate-800/50 rounded text-xs text-slate-300">
                                {axe}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Input - Mode Texte ou Audio */}
                  {simulationActive && (
                    <div className="p-4 border-t border-slate-700/50">
                      {/* Sélecteur de mode */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {/* Boutons de mode */}
                          <button
                            onClick={() => {
                              if (expertModeActive) stopExpertMode();
                              setSimulationMode("text");
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              simulationMode === "text" 
                                ? "bg-blue-500/30 text-blue-300 border border-blue-500/50"
                                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                            }`}
                          >
                            💬 Texte
                          </button>
                          <button
                            onClick={() => {
                              if (expertModeActive) stopExpertMode();
                              setSimulationMode("audio_beginner");
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              simulationMode === "audio_beginner" 
                                ? "bg-purple-500/30 text-purple-300 border border-purple-500/50"
                                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                            }`}
                          >
                            🎤 Débutant
                          </button>
                          <button
                            onClick={() => {
                              if (isRecording) stopRecording();
                              setSimulationMode("audio_expert");
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              simulationMode === "audio_expert" 
                                ? "bg-gradient-to-r from-orange-500/30 to-red-500/30 text-orange-300 border border-orange-500/50"
                                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                            }`}
                          >
                            🔥 Expert
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          {isPlayingAudio && (
                            <>
                              <span className="flex items-center gap-1 text-emerald-400 text-xs animate-pulse">
                                <Volume2 className="w-4 h-4" /> Fatima parle...
                              </span>
                              <button
                                onClick={stopAudio}
                                className="px-3 py-1 bg-red-500/20 text-red-400 rounded-lg text-xs hover:bg-red-500/30"
                              >
                                Stop
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {simulationMode === "text" ? (
                        /* Mode Texte */
                        <div className="flex gap-3">
                          <input
                            type="text"
                            value={simulationInput}
                            onChange={(e) => setSimulationInput(e.target.value)}
                            onKeyPress={(e) => e.key === "Enter" && sendSimulationMessage()}
                            placeholder="Répondez au prospect..."
                            className="flex-1 bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500"
                            disabled={sendingSimMessage}
                            data-testid="simulation-input"
                          />
                          <button
                            onClick={sendSimulationMessage}
                            disabled={!simulationInput.trim() || sendingSimMessage}
                            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-xl flex items-center gap-2"
                            data-testid="send-sim-btn"
                          >
                            {sendingSimMessage ? (
                              <RefreshCw className="w-5 h-5 animate-spin" />
                            ) : (
                              <Send className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      ) : simulationMode === "audio_beginner" ? (
                        /* Mode Audio Débutant - Push to Talk */
                        <div className="flex flex-col items-center gap-4">
                          {sendingSimMessage ? (
                            <div className="flex items-center gap-3 text-slate-400">
                              <RefreshCw className="w-6 h-6 animate-spin" />
                              <span>Transcription et réponse en cours...</span>
                            </div>
                          ) : isRecording ? (
                            <div className="flex flex-col items-center gap-4">
                              <div className="relative">
                                <div className="w-24 h-24 bg-purple-500/20 rounded-full flex items-center justify-center animate-pulse">
                                  <div className="w-20 h-20 bg-purple-500/30 rounded-full flex items-center justify-center">
                                    <Mic className="w-10 h-10 text-purple-400" />
                                  </div>
                                </div>
                                <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-sm text-purple-300 font-medium whitespace-nowrap">
                                  🔴 Enregistrement...
                                </span>
                              </div>
                              <button
                                onClick={stopRecording}
                                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-xl flex items-center gap-2 font-medium shadow-lg"
                                data-testid="stop-recording-btn"
                              >
                                <Send className="w-5 h-5" /> Envoyer ma réponse
                              </button>
                              <p className="text-xs text-slate-400 text-center">
                                Parlez clairement, puis cliquez sur "Envoyer"
                              </p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-4">
                              <button
                                onClick={startRecording}
                                disabled={isPlayingAudio}
                                className="px-10 py-5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-2xl flex items-center gap-3 font-medium text-lg transition-all shadow-xl hover:shadow-purple-500/30"
                                data-testid="start-recording-btn"
                              >
                                <Mic className="w-7 h-7" />
                                Appuyez pour parler
                              </button>
                              <p className="text-xs text-slate-500 text-center max-w-xs">
                                {isPlayingAudio 
                                  ? "⏳ Attendez que Fatima finisse de parler..."
                                  : "Maintenez, parlez, puis cliquez sur 'Envoyer'"
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Mode Audio Expert - Conversation fluide */
                        <div className="flex flex-col items-center gap-4">
                          {sendingSimMessage ? (
                            <div className="flex items-center gap-3 text-slate-400">
                              <RefreshCw className="w-6 h-6 animate-spin" />
                              <span>Traitement...</span>
                            </div>
                          ) : expertModeActive ? (
                            <div className="flex flex-col items-center gap-4">
                              <div className="relative">
                                <div className={`w-28 h-28 rounded-full flex items-center justify-center transition-all duration-200 ${
                                  isSpeaking 
                                    ? "bg-gradient-to-r from-orange-500/40 to-red-500/40 scale-110" 
                                    : "bg-orange-500/20"
                                }`}>
                                  <div className={`w-24 h-24 rounded-full flex items-center justify-center transition-all duration-200 ${
                                    isSpeaking 
                                      ? "bg-gradient-to-r from-orange-500/50 to-red-500/50" 
                                      : "bg-orange-500/30"
                                  }`}>
                                    <Mic className={`w-12 h-12 transition-colors ${isSpeaking ? "text-orange-300" : "text-orange-400"}`} />
                                  </div>
                                </div>
                                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full ${isSpeaking ? "bg-orange-400 animate-pulse" : "bg-slate-500"}`} />
                                  <span className="text-xs text-orange-300 font-medium">
                                    {isSpeaking ? "Vous parlez..." : "En écoute..."}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="text-center">
                                <p className="text-sm text-slate-300 mb-2">
                                  🔥 <strong>Mode Expert</strong> - Conversation fluide
                                </p>
                                <p className="text-xs text-slate-500 max-w-sm">
                                  Parlez naturellement. L'envoi se fait automatiquement après 1.5s de silence.
                                </p>
                              </div>
                              
                              <button
                                onClick={stopExpertMode}
                                className="px-6 py-2.5 bg-red-600/80 hover:bg-red-600 rounded-xl flex items-center gap-2 text-sm font-medium"
                              >
                                <X className="w-4 h-4" /> Arrêter le mode Expert
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-4">
                              <div className="text-center mb-2">
                                <p className="text-sm text-orange-300 font-medium mb-1">🔥 Mode Expert</p>
                                <p className="text-xs text-slate-400 max-w-xs">
                                  Conversation fluide comme un vrai appel téléphonique. 
                                  L'envoi se déclenche automatiquement après chaque phrase.
                                </p>
                              </div>
                              <button
                                onClick={startExpertMode}
                                disabled={isPlayingAudio}
                                className="px-10 py-5 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-2xl flex items-center gap-3 font-medium text-lg transition-all shadow-xl hover:shadow-orange-500/30"
                                data-testid="start-expert-btn"
                              >
                                <Phone className="w-7 h-7" />
                                Lancer la conversation
                              </button>
                              <p className="text-xs text-slate-500 text-center max-w-xs">
                                {isPlayingAudio 
                                  ? "⏳ Attendez que Fatima finisse..."
                                  : "Parlez naturellement, l'IA détecte automatiquement vos pauses"
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* EXERCICES PRATIQUES */}
        {activeTab === "exercises" && (
          <div className="space-y-6" data-testid="exercises-tab">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Target className="w-6 h-6 text-orange-400" />
                  Exercices Pratiques
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                  Entraînez-vous au pitch et au traitement des objections avec feedback IA
                </p>
              </div>
            </div>

            {/* Sous-onglets */}
            <div className="flex gap-2">
              <button
                onClick={() => setExerciseTab("pitch")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  exerciseTab === "pitch" ? "bg-orange-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                }`}
              >
                🎤 Pitch 90 secondes
              </button>
              <button
                onClick={() => {
                  setExerciseTab("objections");
                  if (objectionScenarios.length === 0) loadObjectionScenarios();
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  exerciseTab === "objections" ? "bg-orange-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                }`}
              >
                💬 Traitement objections
              </button>
            </div>

            {/* PITCH 90 SECONDES */}
            {exerciseTab === "pitch" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Mic className="w-5 h-5 text-orange-400" />
                    Votre Pitch MaPrimeAdapt'
                  </h3>
                  
                  {/* Timer + Bouton principal */}
                  <div className="mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Timer className="w-5 h-5 text-slate-400" />
                      <span className={`text-3xl font-mono ${pitchDuration > 100 ? "text-red-400" : pitchDuration > 70 ? "text-emerald-400" : "text-slate-300"}`}>
                        {Math.floor(pitchDuration / 60)}:{(pitchDuration % 60).toString().padStart(2, '0')}
                      </span>
                      <span className="text-xs text-slate-500">(objectif: 70-100s)</span>
                    </div>
                  </div>
                  
                  {/* Bouton principal */}
                  {!pitchListening ? (
                    <button
                      onClick={startPitchSpeech}
                      disabled={evaluatingPitch}
                      className="w-full py-5 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 rounded-xl text-lg font-bold flex items-center justify-center gap-3 shadow-lg shadow-red-500/30 transition-all transform hover:scale-[1.02]"
                    >
                      <Mic className="w-6 h-6" />
                      🎤 Démarrer le pitch
                    </button>
                  ) : (
                    <button
                      onClick={stopPitchSpeech}
                      className="w-full py-5 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-500 hover:to-slate-600 rounded-xl text-lg font-bold flex items-center justify-center gap-3 animate-pulse"
                    >
                      <Square className="w-6 h-6" />
                      ⏹️ Arrêter et évaluer
                    </button>
                  )}
                  
                  {/* Indicateur d'écoute */}
                  {pitchListening && (
                    <div className="mt-4 p-4 bg-red-900/30 border border-red-500/50 rounded-xl">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-red-400 font-medium">🎙️ Parlez maintenant...</span>
                      </div>
                      <p className="text-xs text-slate-400">Votre texte apparaît en direct ci-dessous</p>
                    </div>
                  )}
                  
                  {/* Transcription en direct */}
                  <div className="mt-4 p-4 bg-slate-900/50 border border-slate-700 rounded-xl min-h-[150px]">
                    <p className="text-xs text-slate-500 mb-2">📝 Transcription en direct :</p>
                    <p className="text-slate-200 whitespace-pre-wrap">
                      {pitchText}
                      {pitchInterimText && (
                        <span className="text-slate-400 italic">{pitchInterimText}</span>
                      )}
                      {!pitchText && !pitchInterimText && !pitchListening && (
                        <span className="text-slate-500 italic">Cliquez sur "Démarrer le pitch" et parlez...</span>
                      )}
                    </p>
                  </div>
                  
                  {/* Compteur de mots */}
                  {pitchText && (
                    <p className="mt-2 text-sm text-center text-slate-400">
                      {pitchText.split(/\s+/).filter(w => w).length} mots
                    </p>
                  )}
                  
                  {/* Indicateur d'évaluation */}
                  {evaluatingPitch && (
                    <div className="mt-4 p-4 bg-orange-900/30 border border-orange-500/50 rounded-xl flex items-center justify-center gap-3">
                      <RefreshCw className="w-5 h-5 animate-spin text-orange-400" />
                      <span className="text-orange-400">Fatima évalue votre pitch...</span>
                    </div>
                  )}
                </div>

                {/* Résultat */}
                <div className="space-y-4">
                  {pitchResult ? (
                    <>
                      <div className={`rounded-xl p-5 border ${
                        pitchResult.badge === "gold" ? "bg-yellow-900/30 border-yellow-500/50" :
                        pitchResult.badge === "silver" ? "bg-slate-700/50 border-slate-400/50" :
                        pitchResult.badge === "bronze" ? "bg-orange-900/30 border-orange-500/50" :
                        "bg-slate-800/50 border-slate-700/50"
                      }`}>
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-4xl">
                            {pitchResult.badge === "gold" ? "🥇" : pitchResult.badge === "silver" ? "🥈" : pitchResult.badge === "bronze" ? "🥉" : "📝"}
                          </span>
                          <div>
                            <h3 className="font-bold text-lg">Note : {pitchResult.note_globale}/100</h3>
                            <p className="text-sm opacity-80">Badge : {pitchResult.badge?.toUpperCase()}</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                        <h4 className="font-semibold mb-3">Détail des scores</h4>
                        <div className="space-y-2">
                          {Object.entries(pitchResult.scores || {}).map(([key, value]) => (
                            <div key={key} className="flex justify-between items-center">
                              <span className="text-sm text-slate-400 capitalize">{key.replace(/_/g, ' ')}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${value >= 16 ? "bg-emerald-500" : value >= 12 ? "bg-yellow-500" : "bg-red-500"}`}
                                    style={{ width: `${(value / 20) * 100}%` }}
                                  />
                                </div>
                                <span className="text-sm font-medium w-10">{value}/20</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {pitchResult.conseil_personnalise && (
                        <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-4">
                          <h4 className="font-semibold text-purple-300 mb-2">💡 Conseil de Fatima</h4>
                          <p className="text-sm text-slate-300">{pitchResult.conseil_personnalise}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 text-center">
                      <Brain className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">Soumettez votre pitch pour recevoir une évaluation détaillée de Fatima</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TRAITEMENT DES OBJECTIONS */}
            {exerciseTab === "objections" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-orange-400" />
                    Choisissez une objection
                  </h3>
                  
                  {objectionScenarios.map(scenario => (
                    <button
                      key={scenario.scenario_id}
                      onClick={() => {
                        setSelectedObjection(scenario);
                        setObjectionResponse("");
                        setObjectionResult(null);
                      }}
                      className={`w-full text-left p-4 rounded-xl border transition-colors ${
                        selectedObjection?.scenario_id === scenario.scenario_id
                          ? "bg-orange-900/30 border-orange-500/50"
                          : "bg-slate-800/50 border-slate-700/50 hover:border-slate-600"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">"{scenario.objection}"</p>
                          <p className="text-xs text-slate-500 mt-1">Type: {scenario.type} • Difficulté: {scenario.difficulty}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          scenario.difficulty === "easy" ? "bg-emerald-900/50 text-emerald-400" :
                          scenario.difficulty === "medium" ? "bg-yellow-900/50 text-yellow-400" :
                          "bg-red-900/50 text-red-400"
                        }`}>
                          {scenario.difficulty}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="space-y-4">
                  {selectedObjection ? (
                    <>
                      <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
                        <p className="text-sm text-red-300 mb-1">Le prospect dit :</p>
                        <p className="font-semibold text-lg">"{selectedObjection.objection}"</p>
                      </div>

                      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                        <label className="block text-sm text-slate-400 mb-2">Votre réponse :</label>
                        <textarea
                          value={objectionResponse}
                          onChange={(e) => setObjectionResponse(e.target.value)}
                          placeholder="Comment répondez-vous à cette objection ?"
                          className="w-full h-32 p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-sm resize-none focus:outline-none focus:border-orange-500"
                        />
                        <button
                          onClick={submitObjectionResponse}
                          disabled={evaluatingObjection || !objectionResponse.trim()}
                          className="mt-3 w-full py-2.5 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 rounded-lg font-medium flex items-center justify-center gap-2"
                        >
                          {evaluatingObjection ? (
                            <><RefreshCw className="w-4 h-4 animate-spin" /> Évaluation...</>
                          ) : (
                            <><Send className="w-4 h-4" /> Évaluer ma réponse</>
                          )}
                        </button>
                      </div>

                      {objectionResult && (
                        <>
                          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                            <h4 className="font-semibold mb-3 flex items-center gap-2">
                              <Award className="w-5 h-5 text-orange-400" />
                              Note : {objectionResult.note_globale}/100
                            </h4>
                            <div className="space-y-2">
                              {Object.entries(objectionResult.scores || {}).map(([key, value]) => (
                                <div key={key} className="flex justify-between items-center">
                                  <span className="text-sm text-slate-400 capitalize">{key.replace(/_/g, ' ')}</span>
                                  <span className={`font-medium ${value >= 16 ? "text-emerald-400" : value >= 12 ? "text-yellow-400" : "text-red-400"}`}>
                                    {value}/20
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {objectionResult.conseil && (
                            <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-4">
                              <h4 className="font-semibold text-purple-300 mb-2">💡 Conseil</h4>
                              <p className="text-sm text-slate-300">{objectionResult.conseil}</p>
                            </div>
                          )}

                          <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-xl p-4">
                            <h4 className="font-semibold text-emerald-300 mb-2">✅ Réponse idéale</h4>
                            <p className="text-sm text-slate-300">{selectedObjection.reponse_ideale}</p>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 text-center">
                      <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">Sélectionnez une objection pour vous entraîner</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* DOCUMENTS */}
        {activeTab === "documents" && (
          <div className="space-y-6" data-testid="documents-tab">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FolderOpen className="w-6 h-6 text-amber-400" />
                  Ressources & Documents
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                  Tous les documents utiles pour maîtriser MaPrimeAdapt' et le processus SANI ADAPT
                </p>
              </div>
              <button
                onClick={loadDocuments}
                disabled={documentsLoading}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2 text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${documentsLoading ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
            </div>

            {/* Filtres par catégorie */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedDocCategory(null)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  !selectedDocCategory ? "bg-purple-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                }`}
              >
                Tous
              </button>
              {["process", "r1", "eligibilite", "formation"].map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedDocCategory(cat)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    selectedDocCategory === cat ? "bg-purple-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                  }`}
                >
                  {cat === "process" && "📋 Process"}
                  {cat === "r1" && "🔧 R1 Technicien"}
                  {cat === "eligibilite" && "✅ Éligibilité"}
                  {cat === "formation" && "📚 Formation"}
                </button>
              ))}
            </div>

            {documentsLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-purple-400" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents
                  .filter(doc => !selectedDocCategory || doc.category === selectedDocCategory)
                  .map(doc => (
                  <div
                    key={doc.doc_id}
                    className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 hover:border-purple-500/50 transition-all"
                    data-testid={`doc-${doc.doc_id}`}
                  >
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="text-3xl">{doc.icon}</div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-white">{doc.title}</h3>
                        <p className="text-xs text-slate-400 mt-0.5">{doc.description}</p>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {doc.tags?.slice(0, 4).map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-slate-700/50 rounded text-xs text-slate-400"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Contenu résumé */}
                    {doc.content_summary && (
                      <div className="mb-4 p-3 bg-slate-900/50 rounded-lg max-h-[200px] overflow-y-auto">
                        <p className="text-xs text-slate-300 whitespace-pre-wrap">{doc.content_summary}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      {doc.file_url && (
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                          data-testid={`download-${doc.doc_id}`}
                        >
                          <Download className="w-4 h-4" />
                          Télécharger PDF
                        </a>
                      )}
                      {doc.type === "guide" && (
                        <button
                          className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                        >
                          <FileCheck className="w-4 h-4" />
                          Voir le guide
                        </button>
                      )}
                    </div>

                    {/* Audience */}
                    <div className="mt-3 pt-3 border-t border-slate-700/50">
                      <p className="text-xs text-slate-500">
                        Pour : {doc.audience?.map(a => {
                          if (a === "telepro") return "Téléprospecteurs";
                          if (a === "r1") return "Techniciens R1";
                          if (a === "amo") return "AMO";
                          if (a === "admin") return "Admin";
                          if (a === "all") return "Tous";
                          return a;
                        }).join(", ")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Info box */}
            <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/30 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <HelpCircle className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-300">Besoin d'un document ?</h3>
                  <p className="text-sm text-slate-400 mt-1">
                    Ces ressources sont également disponibles dans la base de connaissances du Comité de Direction.
                    Fatima et les autres agents IA peuvent vous expliquer ces documents en détail.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CALCULATEUR DE RESTE À CHARGE */}
        {activeTab === "calculateur" && (
          <div className="space-y-6" data-testid="calculateur-tab">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Calculator className="w-6 h-6 text-emerald-400" />
                  Calculateur de Reste à Charge
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                  Entraînez-vous à calculer le reste à charge selon le profil du prospect
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Formulaire de saisie */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-400" />
                  Paramètres du projet
                </h3>

                <div className="space-y-4">
                  {/* Devis HT */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Montant du devis HT (€)</label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="number"
                        value={calcDevisHT}
                        onChange={(e) => setCalcDevisHT(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg focus:outline-none focus:border-purple-500"
                        placeholder="10000"
                        data-testid="calc-devis-ht"
                      />
                    </div>
                  </div>

                  {/* Zone géographique */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Zone géographique</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCalcZone("province")}
                        className={`flex-1 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                          calcZone === "province" 
                            ? "bg-purple-600 text-white" 
                            : "bg-slate-900/50 border border-slate-700 text-slate-400 hover:bg-slate-800"
                        }`}
                        data-testid="calc-zone-province"
                      >
                        <MapPin className="w-4 h-4" />
                        Province
                      </button>
                      <button
                        onClick={() => setCalcZone("idf")}
                        className={`flex-1 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                          calcZone === "idf" 
                            ? "bg-purple-600 text-white" 
                            : "bg-slate-900/50 border border-slate-700 text-slate-400 hover:bg-slate-800"
                        }`}
                        data-testid="calc-zone-idf"
                      >
                        <MapPin className="w-4 h-4" />
                        Île-de-France
                      </button>
                    </div>
                  </div>

                  {/* Nombre de personnes */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Nombre de personnes dans le foyer fiscal</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(n => (
                        <button
                          key={n}
                          onClick={() => setCalcPersonnes(n)}
                          className={`flex-1 py-2.5 rounded-lg font-medium transition-colors ${
                            calcPersonnes === n 
                              ? "bg-purple-600 text-white" 
                              : "bg-slate-900/50 border border-slate-700 text-slate-400 hover:bg-slate-800"
                          }`}
                          data-testid={`calc-personnes-${n}`}
                        >
                          {n}{n === 5 ? "+" : ""}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Revenus */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Revenus fiscaux de référence (€/an)</label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="number"
                        value={calcRevenus}
                        onChange={(e) => setCalcRevenus(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg focus:outline-none focus:border-purple-500"
                        placeholder="18000"
                        data-testid="calc-revenus"
                      />
                    </div>
                    {calcResult && (
                      <p className="text-xs text-slate-500 mt-1">
                        Plafond {calcResult.profil === "tres_modeste" ? "Très modeste" : "Modeste"} ({calcResult.zone}, {calcResult.personnes} pers.) : {calcResult.plafondUtilise?.toLocaleString()}€
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Résultat du calcul */}
              <div className="space-y-4">
                {/* Profil */}
                {calcResult && (
                  <>
                    <div className={`rounded-xl p-5 border ${
                      calcResult.eligible 
                        ? calcResult.profil === "tres_modeste" 
                          ? "bg-blue-900/30 border-blue-500/50" 
                          : "bg-yellow-900/30 border-yellow-500/50"
                        : "bg-red-900/30 border-red-500/50"
                    }`}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-4xl">{calcResult.emoji}</span>
                        <div>
                          <h3 className="font-bold text-lg">
                            {calcResult.eligible 
                              ? calcResult.profil === "tres_modeste" ? "Profil BLEU - Très Modeste" : "Profil JAUNE - Modeste"
                              : "NON ÉLIGIBLE MaPrimeAdapt'"
                            }
                          </h3>
                          <p className="text-sm opacity-80">
                            {calcResult.eligible 
                              ? `Taux de subvention : ${calcResult.taux}%`
                              : "Revenus supérieurs aux plafonds Modeste"
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    {calcResult.eligible && (
                      <>
                        {/* Tableau de calcul */}
                        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                          <h3 className="font-semibold mb-4 flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-emerald-400" />
                            Détail du calcul
                          </h3>

                          <div className="space-y-3">
                            <div className="flex justify-between py-2 border-b border-slate-700/50">
                              <span className="text-slate-400">Devis HT</span>
                              <span className="font-medium">{calcResult.devisHT.toLocaleString()} €</span>
                            </div>
                            <div className="flex justify-between py-2 border-b border-slate-700/50">
                              <span className="text-slate-400">+ TVA (10%)</span>
                              <span className="font-medium">+ {calcResult.tva.toLocaleString()} €</span>
                            </div>
                            <div className="flex justify-between py-2 border-b border-slate-700/50 bg-slate-900/30 -mx-5 px-5">
                              <span className="font-semibold">= Total TTC</span>
                              <span className="font-bold">{calcResult.totalTTC.toLocaleString()} €</span>
                            </div>
                            <div className="flex justify-between py-2 border-b border-slate-700/50 text-emerald-400">
                              <span>- Subvention ANAH ({calcResult.taux}% du HT)</span>
                              <span className="font-medium">- {calcResult.subventionANAH.toLocaleString()} €</span>
                            </div>
                            <div className="flex justify-between py-2 bg-purple-900/30 -mx-5 px-5 rounded-lg">
                              <span className="font-bold text-purple-300">= Reste à Charge Total</span>
                              <span className="font-bold text-xl text-purple-300">{calcResult.resteAChargeTotal.toLocaleString()} €</span>
                            </div>
                          </div>
                        </div>

                        {/* Décomposition du RAC */}
                        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                          <h3 className="font-semibold mb-4 flex items-center gap-2">
                            <Target className="w-5 h-5 text-amber-400" />
                            Décomposition du reste à charge
                          </h3>

                          <div className="space-y-3">
                            <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
                              <div>
                                <span className="text-slate-300">Participation client</span>
                                <p className="text-xs text-slate-500">Payée à la R3 (moyenne réelle)</p>
                              </div>
                              <span className="font-bold text-lg text-amber-400">~{calcResult.participationMoyenne.toLocaleString()} €</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
                              <div>
                                <span className="text-slate-300">Subventions complémentaires</span>
                                <p className="text-xs text-slate-500">Caisses retraite, département, mutuelles...</p>
                              </div>
                              <span className="font-medium text-emerald-400">~{calcResult.subventionsComplementaires.toLocaleString()} €</span>
                            </div>
                          </div>

                          {/* Garantie */}
                          <div className="mt-4 p-3 bg-emerald-900/30 border border-emerald-500/30 rounded-lg">
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="font-semibold text-emerald-300">Garantie de Résultat SANI ADAPT</p>
                                <p className="text-sm text-emerald-400/80">
                                  Si nous ne trouvons pas les subventions complémentaires, le client ne paie pas la différence !
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {!calcResult.eligible && (
                      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
                          <div>
                            <h4 className="font-semibold text-red-300">Prospect non éligible</h4>
                            <p className="text-sm text-slate-400 mt-1">
                              Les revenus de {parseInt(calcRevenus).toLocaleString()}€ dépassent le plafond Modeste 
                              de {PLAFONDS_2026[calcZone][Math.min(calcPersonnes, 5)].modeste.toLocaleString()}€ 
                              pour {calcPersonnes} personne(s) en {calcZone === "idf" ? "Île-de-France" : "Province"}.
                            </p>
                            <p className="text-sm text-slate-500 mt-2">
                              💡 <strong>Script :</strong> "Je comprends. Malheureusement, vos revenus dépassent les plafonds du dispositif MaPrimeAdapt'. Je vous remercie de votre intérêt et vous souhaite une bonne continuation !"
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Info pédagogique */}
            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-xl p-5">
              <h3 className="font-semibold text-purple-300 mb-3 flex items-center gap-2">
                <HelpCircle className="w-5 h-5" />
                Rappel des règles de calcul
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="bg-slate-900/30 rounded-lg p-3">
                  <p className="font-medium text-blue-300 mb-1">🔵 Très Modeste (70%)</p>
                  <p className="text-slate-400">Participation moyenne : ~2 276€</p>
                </div>
                <div className="bg-slate-900/30 rounded-lg p-3">
                  <p className="font-medium text-yellow-300 mb-1">🟡 Modeste (50%)</p>
                  <p className="text-slate-400">Participation moyenne : ~3 933€</p>
                </div>
                <div className="bg-slate-900/30 rounded-lg p-3">
                  <p className="font-medium text-red-300 mb-1">❌ Non éligible</p>
                  <p className="text-slate-400">Revenus &gt; plafond Modeste</p>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                ⚠️ La subvention ANAH est calculée sur le montant HT, mais le client paie le TTC (HT + TVA 10%).
              </p>
            </div>
          </div>
        )}

        {/* ANALYTICS */}
        {activeTab === "analytics" && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold">Analyse de performance</h2>

            <div className="grid grid-cols-2 gap-6">
              {/* Analyse des appels */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-blue-400" /> Analyse de vos appels
                </h3>
                <p className="text-sm text-slate-400 mb-4">
                  Fatima analyse vos derniers appels transcrits et vous donne des conseils personnalisés.
                </p>
                <button
                  onClick={analyzeMyalls}
                  disabled={analyzingCalls}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg flex items-center justify-center gap-2"
                >
                  {analyzingCalls ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" /> Analyse en cours...
                    </>
                  ) : (
                    <>
                      <Brain className="w-5 h-5" /> Analyser mes appels
                    </>
                  )}
                </button>

                {callAnalysis && (
                  <div className="mt-4 bg-slate-900/50 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                    <div className="prose prose-invert prose-sm">
                      <div className="whitespace-pre-wrap text-slate-300">{callAnalysis.analysis}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Compétences */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-400" /> Compétences
                </h3>
                <div className="space-y-4">
                  {["prospection", "objections", "closing", "communication", "outils"].map((skill, i) => {
                    const level = progress?.skills?.[skill] || Math.floor(Math.random() * 60) + 20;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="capitalize">{skill}</span>
                          <span className="text-slate-400">{level}%</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                            style={{ width: `${level}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Historique des formations */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-400" /> Historique des formations
              </h3>
              {badges.length > 0 ? (
                <div className="space-y-3">
                  {badges.map((badge, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                          <Award className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                          <p className="font-medium">{badge.title}</p>
                          <p className="text-sm text-slate-400">{badge.earned_at ? new Date(badge.earned_at).toLocaleDateString('fr-FR') : 'Récemment'}</p>
                        </div>
                      </div>
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-slate-500 py-8">
                  <Award className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucune formation terminée pour l'instant</p>
                  <p className="text-sm">Complétez des modules pour obtenir des badges !</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
